import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Stack, TextField, Button } from '@mui/material';
import Link from '../../../../theme/components/Link';
import Polyglot from 'node-polyglot';
import { RouterComponentProps, withRouter } from '../../../../util/route-dom';
import withHandlingErrors, {
  HandlingErrorWrappedProps,
} from '../../../../handlingErrors';
import { DeviceDataAPIResponse } from '../../../../redux/commander/api/device.model';
import GroupModel from '../../../../redux/groups/api/GroupModel';
import {
  fetchDeviceByCloudConnectId,
  postAssociatedDevice,
} from '../../../../redux/commander/actions/thunks';
import { STModal } from '../../../commons/Modal';
import {
  getAssociatedDevices,
  getNumDevices,
} from '../../../../redux/commander/selectors';
import {
  getDictionaryGroups,
  getGroups,
} from '../../../../redux/groups/selectors';
import { RootState } from '../../../../redux/store.model';
import { withPolyglot } from '../../../../i18n';
import StoerkIdInsufficientDevicesQuotaModal from '../../../StoerkId/MyStoerkId/StoerkIdInsufficientDevicesQuotaModal';
import { getMyWorkspaceSelector } from '../../../../redux/stoerkID/selectors/StoerkId.selectors';
import { stoerkIdInsufficientDevicesQuotaSelector } from '../../../../redux/stoerkID/selectors/StoerkIdRestrictions.selectors';
import {
  withShowMessage,
  ShowMessageProps,
} from '../../../HOC/withShowMessage';
import LoadingButton from '@mui/lab/LoadingButton';

interface State {
  cloudConnectID?: string;
  loading?: boolean;
}

type DeviceAddProps = {
  polyglot: Polyglot;
  showAddDevice: boolean;
  closeAddDevice(...args: unknown[]): unknown;
} & RouterComponentProps &
  ShowMessageProps &
  ConnectedComponentProps &
  HandlingErrorWrappedProps;

export class DeviceAdd extends Component<DeviceAddProps, State> {
  constructor(props: DeviceAddProps) {
    super(props);
    this.setCloudConnectID = this.setCloudConnectID.bind(this);
    this.associateDevice = this.associateDevice.bind(this);
    this.showSuccessfulMessage = this.showSuccessfulMessage.bind(this);
    this.getGroupsDeviceAlreadyAdded =
      this.getGroupsDeviceAlreadyAdded.bind(this);
    this.state = {
      loading: false,
    };
  }

  /**
   * Set cloud connect id
   * @param object event
   */
  setCloudConnectID(event: { target: { value: string } }) {
    const id = event.target.value.trim();
    this.setState({ cloudConnectID: id });
  }

  /**
   * Get groups devices already added
   * This function returns a list with the groups (including the route to it) where
   * the device is associated
   * @param int totalDevicesAdded
   * @return array routes
   */
  getGroupsDeviceAlreadyAdded(
    deviceAlreadyAdded: boolean,
    devicePreview: DeviceDataAPIResponse | undefined
  ): JSX.Element[] {
    const { groups, dictionaryGroups } = this.props;
    if (!deviceAlreadyAdded) {
      return [];
    }
    const groupsIds = GroupModel.getGroupsIdByDeviceId(
      devicePreview?.uuid,
      dictionaryGroups ?? {}
    );
    return groupsIds.map((groupId) => {
      const groupDevice = dictionaryGroups[groupId];
      const routeParent = GroupModel.getPathToGroup(
        groupDevice,
        dictionaryGroups
      );

      return routeParent?.length ? (
        <Link to={`/devicemanager/group/${groupId}`}>
          {routeParent.map((r: { name: any }) => r.name).join(' • ')}
        </Link>
      ) : (
        <></>
      );
    });
  }

  /**
   * Show successful message
   * @param int totalDevicesAdded
   * @return string message
   */
  showSuccessfulMessage(
    devicePreview: DeviceDataAPIResponse | undefined,
    deviceAlreadyAdded: boolean
  ) {
    const { polyglot, showMessage } = this.props;
    const text = deviceAlreadyAdded
      ? polyglot
          .t('device.add_already_added_message')
          .replace(
            '#text',
            devicePreview && `"${devicePreview.name}"` ? devicePreview.name : ''
          )
      : polyglot
          .t('device.add_successful_message')
          .replace(
            '#text',
            devicePreview && devicePreview.name ? `"${devicePreview.name}"` : ''
          );
    showMessage(text, {
      variant: deviceAlreadyAdded ? 'warning' : 'success',
    });
    const list = this.getGroupsDeviceAlreadyAdded(
      deviceAlreadyAdded,
      devicePreview
    );
    if (deviceAlreadyAdded && list.length > 0)
      return polyglot
        .t('device.add_already_added_messages_with_groups')
        .replace(
          '#text',
          devicePreview && `"${devicePreview.name}"` ? devicePreview.name : ''
        );
    if (deviceAlreadyAdded && list.length === 0)
      return polyglot
        .t('device.add_already_added_message')
        .replace(
          '#text',
          devicePreview && `"${devicePreview.name}"` ? devicePreview.name : ''
        );
    return null;
  }

  /**
   * Associate device:
   * this function does a rest call to backed to associate a device to the user account
   */
  async associateDevice() {
    const { closeAddDevice, handlingErrorsApi, associatedDevicesIds } =
      this.props;
    const { cloudConnectID } = this.state;
    this.setState({ loading: true });
    if (!cloudConnectID) {
      this.setState({ loading: false });
      return;
    }
    try {
      const devicePreview = await this.getDeviceByCloudId(cloudConnectID);
      const deviceAlreadyAdded = !!associatedDevicesIds?.includes(
        devicePreview?.uuid || ''
      );
      await this.props.postAssociatedDevice(cloudConnectID);
      const message = this.showSuccessfulMessage(
        devicePreview,
        deviceAlreadyAdded
      );
      closeAddDevice(
        message,
        this.getGroupsDeviceAlreadyAdded(deviceAlreadyAdded, devicePreview)
      );
    } catch (error) {
      /* show snack bar with successful message */
      handlingErrorsApi(error);
    }
    this.setState({ loading: false });
  }

  /**
   * Get device by cloud id
   * @param string cloudConnectID
   * @return object device
   */
  async getDeviceByCloudId(
    cloudConnectID: string
  ): Promise<DeviceDataAPIResponse | undefined> {
    const { polyglot } = this.props;
    if (cloudConnectID === null || !cloudConnectID.trim()) {
      throw new Error(polyglot.t('error.empty_ccid'));
    }
    const devicePreview = await fetchDeviceByCloudConnectId(
      cloudConnectID.trim()
    );
    return devicePreview;
  }

  render() {
    const {
      showAddDevice,
      polyglot,
      closeAddDevice,
      workspace,
      showStoerkIdInsufficientDevicesQuota,
    } = this.props;
    const { loading } = this.state;

    if (showStoerkIdInsufficientDevicesQuota)
      return (
        <StoerkIdInsufficientDevicesQuotaModal
          open={showAddDevice}
          onClose={closeAddDevice}
          workspace={workspace}
        />
      );

    return (
      <div>
        <STModal
          open={showAddDevice}
          title={polyglot.t('device.add_dialog_title')}
          onClose={() => closeAddDevice()}
          disabledClose={loading}
          fullWidth
          maxWidth="sm"
          buttonActions={
            <>
              <Button
                disabled={loading}
                type="button"
                onClick={() => closeAddDevice()}
                variant="text"
              >
                {polyglot.t('device.cancel_button_title')}
              </Button>
              <LoadingButton
                loading={loading}
                type="button"
                disabled={this.state.cloudConnectID === undefined}
                variant="contained"
                onClick={this.associateDevice}
              >
                {polyglot.t('device.add_button_title')}
              </LoadingButton>
            </>
          }
        >
          <Stack spacing={2}>
            <TextField
              label={polyglot.t('device.connect_id')}
              onChange={this.setCloudConnectID}
              required
            />
          </Stack>
        </STModal>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  showStoerkIdInsufficientDevicesQuota:
    stoerkIdInsufficientDevicesQuotaSelector(state),
  workspace: getMyWorkspaceSelector(state),
  associatedDevicesIds: getAssociatedDevices(state),
  groups: getGroups(state),
  dictionaryGroups: getDictionaryGroups(state),
});

const connector = connect(mapStateToProps, { postAssociatedDevice });
type ConnectedComponentProps = ConnectedProps<typeof connector>;

export default withHandlingErrors(
  withShowMessage(withPolyglot(withRouter(connector(DeviceAdd))))
);
